/**
 * Validation des biens en EN PRÉPARATION ou DISPONIBLE.
 *
 * Demande CEO (réunion 2026-05-04, spec affinée 2026-05-07) : dès qu'un bien
 * entre dans le périmètre "actif" (en cours de préparation OU déjà disponible
 * à la location), on doit forcer la complétude des champs critiques. Sans eux,
 * la chaîne aval (Suivi ELS, Planning Photo, Mailing) est cassée.
 *
 * Deux axes INDÉPENDANTS depuis le 2026-09-15 (plan « deux cycles »,
 * docs/plan-deux-cycles-2026-09-15.md) :
 *
 * - **Identité** (`ACTIVE_REQUIRED_FIELDS`, rouges) : ce qu'il faut pour
 *   travailler le bien sur n'importe quel axe — publication, mailing, cycle.
 *   Sans eux, le bien reste chez le C-Level (« Identité à compléter »).
 * - **Cycle de sortie** (`getCycleRequiredFields`, rouges aussi) : n'existe
 *   QUE si un cycle est ouvert (`isCycleOuvert`), et n'exige que ce que les
 *   process lancés réclament — collaborateur si ELS, expert si PEB. Un bien
 *   sans cycle n'a rien à remplir ici, et n'est bloqué nulle part par ça.
 * - **Mailing** (15 + conditionnels oranges) : nécessaires pour générer un
 *   mailing prospect propre. Sans eux, le bien n'apparait pas dans /mailing.
 *
 * Avant ce jour, `renonRecuLe`/`collaborateurSrs`/`expertPeb` étaient dans la
 * liste globale : les 86 biens location importés d'Immoweb (complets pour la
 * publication) étaient tous « À débloquer » pour un ELS que personne n'avait
 * décidé — cf. le plan pour les chiffres.
 *
 * La validation est purement informationnelle : on n'empêche pas le save
 * (compatible avec le pattern autosave debouncé existant), mais on rend
 * les manquements VISIBLES via 2 bannières + bordures colorées sur les
 * champs concernés.
 */

import { isPebExpiringSoon } from "./pebCycle";

/** Bien partiel en cours d'édition dans BienDetailPage. Tous les champs sont string|undefined.
 *  On reste sur un Record générique pour absorber les divergences Partial<Bien> vs form-state
 *  (typage `string | null | undefined` côté DB, `string | undefined` côté form). */
export type BienDraft = Record<string, unknown>;

export type MissingLevel = "required" | "mailing";

export interface PrepValidationResult {
  /** Vrai si le bien est dans le périmètre de cette validation (EN PRÉPARATION ou DISPONIBLE). */
  isInPrep: boolean;
  /** Champs obligatoires manquants (identité + cycle ouvert). */
  requiredMissing: string[];
  /** Champs mailing manquants (peut être vide). Inclut les conditionnels actifs. */
  mailingMissing: string[];
  /** Champs du cycle à compléter, NON bloquants (prestataire ELS, expert PEB) — orange. */
  cycleTodoMissing: string[];
  /** Map fieldKey → level pour rendu visuel rapide dans le formulaire. */
  byField: Map<string, MissingLevel>;
}

/**
 * Identité du bien — obligatoire au passage EN PRÉPARATION ou DISPONIBLE,
 * quel que soit l'axe. Cf. transcript CEO 2026-05-04 : "si on loupe des
 * champs à cet endroit-là, en fait ça passe pas dans la suite chez les autres".
 *
 * Alignée sur `publishGate.ts` : tout ce qui est exigé ici l'est aussi par le
 * bouton « Publier » (sauf typeBien/transaction/adresse, structurels), donc
 * un bien qui manque d'un de ces champs n'est de toute façon publiable nulle
 * part — le retenir chez le C-Level n'est pas un faux blocage (décision Q3,
 * 2026-09-15).
 *
 * `renonRecuLe` et `collaborateurSrs` en sont SORTIS le 2026-09-15 : ce sont
 * des champs du cycle de sortie, exigés seulement par `getCycleRequiredFields`.
 */
export const ACTIVE_REQUIRED_FIELDS = [
  "referenceInterne",  // Référence interne
  "projet",            // Projet
  "seraDispoPour",     // Libre le — alimente aussi la publication (availableFrom)
  "typeBien",          // Type de bien
  "transaction",       // Transaction (location / vente)
  "adresse",           // Adresse
  "ville",             // Commune
  "etage",             // Étage
] as const;

/**
 * Le cycle se déroule-t-il SANS état des lieux de sortie ?
 *
 * `elsRequis` (migration 106) est `NOT NULL DEFAULT true` en base. On ne
 * répond vrai que sur un `false` EXPLICITE : `undefined` signifie que la RPC a
 * strippé la colonne pour ce rôle, pas que l'ELS a été annulé.
 */
export function isSansEls(draft: BienDraft): boolean {
  return (draft as Record<string, unknown>).elsRequis === false;
}

/**
 * Jumeau PEB d'`isSansEls` (migration 131) — même contrat : `NOT NULL
 * DEFAULT true`, on ne répond vrai que sur un `false` EXPLICITE.
 */
export function isSansPeb(draft: BienDraft): boolean {
  return (draft as Record<string, unknown>).pebRequis === false;
}

/**
 * Cycle de sortie « en vol » : des marqueurs du cycle ELS/PEB courant sont
 * posés (prestataire assigné, mission lancée, date, statut, refus en attente,
 * clôture collab). Sert à garder la mission prestataire ET le bien visibles
 * (Pipeline) quand le bien repasse LOUÉ AVANT la sortie effective du
 * locataire précédent (relocation anticipée, décision user 2026-07-10 ;
 * élargi 2026-08-20 au flag de lancement de mission, à la simple assignation,
 * puis au refus prestataire — un ELS/PEB déjà assigné, lancé mais pas encore
 * daté, ou REFUSÉ par le prestataire ("ce n'était pas moi") disparaissait
 * quand même du pipeline dès le repassage LOUÉ, alors que le collaborateur
 * doit justement réassigner un nouveau prestataire : le refus met
 * `prestataireEle` à "" ET un trigger serveur (mig. 092, réassignation) remet
 * `elsMissionLanceeAt` à NULL dans la foulée, donc plus AUCUN marqueur ne
 * survivait sans celui-ci) — ces champs ne sont resets qu'au prochain passage
 * LOUÉ → DISPONIBLE (départ suivant).
 *
 * Vit ici (et non dans bienLifecycle.ts, qui le ré-exporte) parce que
 * `isCycleOuvert` en a besoin et que bienLifecycle importe déjà ce module.
 * `statutEls` retiré (mig. 112) : colonne vide sur les 1574 biens, ce terme
 * du OU était toujours faux. `statutEle` porte le vrai statut de l'ELS.
 */
export function hasExitCycleInFlight(draft: BienDraft): boolean {
  const b = draft as Record<string, unknown>;
  return !!(
    b.edlDatePrevue ||
    b.pebDatePrevue ||
    b.statutEle ||
    b.dossierCloture ||
    b.elsMissionLanceeAt ||
    b.pebMissionLanceeAt ||
    b.prestataireEle ||
    b.expertPeb ||
    b.prestataireEleRefuseAt ||
    b.expertPebRefuseAt
  );
}

/**
 * Un cycle de sortie est OUVERT quand le C-Level l'a ouvert : `cycleOuvertLe`
 * (migration 135, Phase 2 du plan « deux cycles ») est posé par le dialogue
 * « Ouvrir un cycle » de la fiche, et seulement par lui. C'est LE fait qui
 * sépare les deux axes : sans cycle ouvert, aucun champ ELS/PEB n'est exigé et
 * le bien n'entre dans aucun pipeline — il se publie, se maile, c'est tout.
 *
 * Ni le renon ni un marqueur isolé n'ouvrent plus un cycle à eux seuls (la
 * Phase 1 les acceptait faute de colonne) : vu en prod le 2026-09-15 sur LS_3,
 * un expert PEB posé pendant des tests suffisait à faire compter le bien dans
 * « Cycles à compléter » alors que son certificat vaut jusqu'en 2036.
 * `hasExitCycleInFlight` garde son rôle propre (relocation anticipée :
 * « quelque chose est en cours ») ; il ne définit pas l'existence du cycle.
 * Remis à NULL au LOUÉ → DISPONIBLE (`resetElsCycleForLoue` + trigger
 * `reset_exit_cycle_on_libere`) et par « vider la fiche de sortie ».
 */
export function isCycleOuvert(draft: BienDraft): boolean {
  // Un bien en VENTE n'a jamais de cycle de sortie (pas de locataire sortant,
  // pas de pipeline ELS & PEB) — même si d'anciens marqueurs traînent en base
  // (constaté en prod le 2026-09-15 : TB_A3.1 comptait dans « Cycles à
  // compléter » à cause d'un prestataire assigné hérité).
  if ((draft as Record<string, unknown>).transaction === "vente") return false;
  const ouvertLe = (draft as Record<string, unknown>).cycleOuvertLe;
  return typeof ouvertLe === "string" && ouvertLe.trim().length > 0;
}

/**
 * ELS et PEB sont deux process AU MÊME NIVEAU (décision Thibaud 2026-09-15 :
 * « il peut y avoir l'un et pas l'autre »). Chacun est « lancé » quand un
 * cycle est ouvert et que sa case n'a pas été explicitement décochée.
 */
export function isElsLance(draft: BienDraft): boolean {
  return isCycleOuvert(draft) && !isSansEls(draft);
}

export function isPebLance(draft: BienDraft): boolean {
  return isCycleOuvert(draft) && !isSansPeb(draft);
}

/**
 * Le PEB de CE cycle est fait : `statutPeb = expertise_ok`, posé par la
 * promotion du nouveau certificat (`promotePebCycle`) ou à la main dans la
 * carte « Renouvellement PEB », et remis à "" au LOUÉ → DISPONIBLE. Jumeau de
 * « ELS fait = statutEle = expertise_ok ». La validité du certificat en
 * vigueur n'est PAS un proxy : un PEB demandé explicitement sur un certificat
 * encore valide reste à faire (HK_H4.4, 2026-09-15).
 */
export function isPebDone(draft: BienDraft): boolean {
  return (draft as Record<string, unknown>).statutPeb === "expertise_ok";
}

/** Champs requis de base = l'identité, sans aucune condition de cycle. */
export function getBaseRequiredFields(_draft: BienDraft): string[] {
  return [...ACTIVE_REQUIRED_FIELDS];
}

/**
 * Champs obligatoires du CYCLE DE SORTIE — vides tant qu'aucun cycle n'est
 * ouvert, puis seulement ce que les process lancés réclament :
 *
 * - `collaborateurSrs` dès qu'un process est lancé — ELS **ou PEB** (décision
 *   Thibaud 2026-09-15 soir : « si on fait un PEB ou un ELS, on doit choisir
 *   un collaborateur responsable du dossier en interne ; pas choisi = pas
 *   complet → Cycle à compléter, invisible du pipeline »). Avant, seul l'ELS
 *   l'exigeait (spec 2026-08-07) : un cycle PEB seul n'avait aucun responsable
 *   interne. Un cycle sans aucun process (dates seules) n'en exige pas.
 * Le prestataire ELS et l'expert PEB n'y sont PLUS (décision Thibaud
 * 2026-09-15 soir : « ils ne sont pas bloquants du moment qu'un collaborateur
 * a été assigné, mais il faut que ce soit tagué dans Cycle à compléter ») —
 * ils vivent dans `getCycleTodoFields`, non bloquants.
 *
 * `renonRecuLe` n'y figure pas : il n'est exigé que par le dialogue « Ouvrir
 * un cycle », et seulement si ELS = oui — un cycle PEB seul (bien vide, sans
 * locataire sortant) n'a pas de renon (Phase 2, 2026-09-15).
 */
export function getCycleRequiredFields(draft: BienDraft): string[] {
  if (!isCycleOuvert(draft)) return [];
  const fields: string[] = [];
  if (isElsLance(draft) || isPebLance(draft)) fields.push("collaborateurSrs");
  return fields;
}

/**
 * Champs « à compléter » du cycle, NON bloquants (décision Thibaud 2026-09-15
 * soir) : avec le collaborateur, le bien entre dans le pipeline ; sans ces
 * champs, il reste listé dans « Cycle à compléter » (chip catalogue, tuile
 * dashboard, tâche C-Level) et les champs sont surlignés en orange.
 *
 * - `prestataireEle` (prestataire assigné) si l'ELS est lancé et pas fait —
 *   un refus prestataire le vide, le bien redevient donc « à compléter ».
 * - `expertPeb` si le PEB est lancé et pas fait — la case « PEB requis » est
 *   la décision, quelle que soit la validité du certificat en vigueur
 *   (HK_H4.4, 2026-09-15). Exception : un nouveau certificat déjà valide
 *   saisi dans le staging (`nouvPebFinLe`/`nouvClassePeb`, demande Thibaud
 *   2026-09-12 — inutile de forcer une assignation qui ne servira à rien).
 */
export function getCycleTodoFields(draft: BienDraft): string[] {
  if (!isCycleOuvert(draft)) return [];
  const fields: string[] = [];
  if (isElsLance(draft) && (draft as Record<string, unknown>).statutEle !== "expertise_ok") {
    fields.push("prestataireEle");
  }
  if (isPebLance(draft) && !isPebDone(draft)) {
    const nouvPebFinLe = typeof draft.nouvPebFinLe === "string" ? draft.nouvPebFinLe : undefined;
    const nouvClassePeb = typeof draft.nouvClassePeb === "string" ? draft.nouvClassePeb : undefined;
    const alreadyCoveredByNewCertificate = !isPebExpiringSoon(nouvPebFinLe, nouvClassePeb);
    if (!alreadyCoveredByNewCertificate) fields.push("expertPeb");
  }
  return fields;
}

/**
 * Champs nécessaires pour générer un mailing prospect propre — inconditionnels.
 * Les 3 champs cave/parking conditionnels sont ajoutés à la volée par
 * `getConditionalMailingFields(draft)` selon les valeurs courantes.
 *
 * Source : colonnes affichées dans MailingPage.tsx + AppartsForRentPage.tsx +
 * spec affinée user 2026-05-07.
 */
export const ACTIVE_MAILING_FIELDS = [
  // Dates contractuelles
  // renonRecuLe deplace dans ACTIVE_REQUIRED_FIELDS (user 2026-05-20).
  "finDeContrat",
  // Tri-état (saisis via SelectField — / Oui / Non) pour permettre la
  // distinction "pas saisi" (orange) vs "Non explicite" (rempli, muted).
  "companyContract",
  "meuble",
  // Intérieur
  "nbChambres",
  "nbSdb",
  "typeSol",
  // Cave / parking / garage (les "Disponible" — les n° et loyers sont conditionnels)
  "caveDisponible",
  "parkingDisponible",
  "garageDisponible",
  // Financier
  "chargesMensuelles",
  "loyerMensuel",
  "chargesIncluses",
  // Énergie
  "classePeb",
  "pebFinLe",
  "consoKwh",
] as const;

/**
 * Retourne la liste des champs mailing conditionnels qui s'activent selon
 * les valeurs courantes de cave/parking. Source des valeurs : src/data/options.ts:54-66.
 *
 * - `caveNumero` : requis si caveDisponible ∈ {"Oui", "Inclus"}
 * - `parkingNumero` : requis si parkingDisponible ∈ {"Oui", "Inclus"}
 * - `parkingLoyerMensuel` : requis seulement si parkingDisponible === "Oui"
 *   (si "Inclus", pas de loyer car déjà inclus dans le loyer principal)
 */
export function getConditionalMailingFields(draft: BienDraft): string[] {
  const conditional: string[] = [];
  const cave = typeof draft.caveDisponible === "string" ? draft.caveDisponible : "";
  const parking = typeof draft.parkingDisponible === "string" ? draft.parkingDisponible : "";
  const garage = typeof draft.garageDisponible === "string" ? draft.garageDisponible : "";

  if (cave === "Oui" || cave === "Inclus") conditional.push("caveNumero");
  if (parking === "Oui" || parking === "Inclus") conditional.push("parkingNumero");
  if (parking === "Oui") conditional.push("parkingLoyerMensuel");
  // Garage : n° requis si Oui/Inclus (pas pour "Possible" — rien d'assigné).
  if (garage === "Oui" || garage === "Inclus") conditional.push("garageNumero");
  // Loyer garage requis si Oui uniquement (si Inclus, déjà dans le loyer principal).
  if (garage === "Oui") conditional.push("garageLoyerMensuel");

  return conditional;
}

/** Considère un champ comme "rempli" : pas vide, pas null/undefined, pas string vide. */
function isFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return !Number.isNaN(value);
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "boolean") return true; // un boolean est toujours "rempli" (true ou false explicite)
  return Boolean(value);
}

/** Normalise la disponibilité et retourne true si elle déclenche la validation
 *  (EN PRÉPARATION ou DISPONIBLE). Le form stocke en kebab ("en_preparation"),
 *  le DB en majuscules — on accepte les deux. */
function isActiveStatus(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const normalized = value
    .toUpperCase()
    .replace(/[_\s]/g, " ")
    .trim();
  return (
    normalized === "EN PREPARATION" ||
    normalized === "EN PRÉPARATION" ||
    normalized === "DISPONIBLE"
  );
}

export function validateBienPrep(draft: BienDraft): PrepValidationResult {
  const isInPrep = isActiveStatus(draft.disponibilite);
  if (!isInPrep) {
    return { isInPrep: false, requiredMissing: [], mailingMissing: [], cycleTodoMissing: [], byField: new Map() };
  }

  const requiredFields = [...getBaseRequiredFields(draft), ...getCycleRequiredFields(draft)];
  const requiredMissing = requiredFields.filter((k) => !isFilled(draft[k]));

  // Inconditionnels + conditionnels actifs
  const mailingFields = [
    ...ACTIVE_MAILING_FIELDS,
    ...getConditionalMailingFields(draft),
  ];
  const mailingMissing = mailingFields.filter((k) => !isFilled(draft[k]));
  const cycleTodoMissing = getCycleTodoFields(draft).filter((k) => !isFilled(draft[k]));

  const byField = new Map<string, MissingLevel>();
  for (const k of requiredMissing) byField.set(k, "required");
  for (const k of mailingMissing) byField.set(k, "mailing");
  // Même signal visuel que le mailing (orange = non bloquant), liste à part.
  for (const k of cycleTodoMissing) byField.set(k, "mailing");

  return { isInPrep, requiredMissing, mailingMissing, cycleTodoMissing, byField };
}
